import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { MagnifyingGlass, User, Buildings, Plus, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Autocompletado contra el CRM de la organización.
 * - Onchange devuelve `{ id, name, email, phone, type, main_license_plate }`
 * - Si no encuentra → botón "Crear cliente rápido" abre modal sin perder progreso del padre.
 *
 * Props:
 *   value: customer object | null
 *   onSelect: (customer) => void
 *   onClear: () => void
 *   testId: prefix for data-testid
 */
export default function CustomerAutocomplete({ value, onSelect, onClear, testId = "cust-auto" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    type: "particular",
    name: "", email: "", phone: "", main_license_plate: "",
    razon_social: "", nif_cif: "", address_fiscal: "",
  });
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (value || !q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: { q, limit: 8 } });
        setResults(data || []); setOpenList(true);
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, value]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (c) => { onSelect(c); setQ(""); setOpenList(false); setResults([]); };
  const startCreate = () => {
    setForm({
      type: "particular",
      name: q.trim(), email: "", phone: "", main_license_plate: "",
      razon_social: "", nif_cif: "", address_fiscal: "",
    });
    setCreating(true);
  };
  const submitCreate = async () => {
    if (!form.name.trim()) { toast.error("Nombre obligatorio"); return; }
    if (form.type === "empresa" && (!form.nif_cif.trim() || !form.razon_social.trim())) {
      toast.error("Para empresa: razón social y CIF obligatorios"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/customers", form);
      toast.success("Cliente creado y vinculado");
      onSelect(data); setCreating(false); setQ("");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (value) {
    return (
      <div className="border border-emerald-400/40 rounded-sm p-2.5 bg-emerald-500/5 flex items-center justify-between gap-3" data-testid={`${testId}-selected`}>
        <div className="flex items-center gap-2 min-w-0">
          {value.type === "empresa" ? <Buildings size={16} weight="fill" className="text-emerald-500 shrink-0" /> : <User size={16} weight="fill" className="text-emerald-500 shrink-0" />}
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{value.name}</div>
            <div className="text-[11px] text-[var(--ag-text-dim)] truncate">
              {value.type === "empresa" ? value.nif_cif : (value.main_license_plate || value.email || value.phone || "—")}
            </div>
          </div>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onClear} className="text-xs h-8" data-testid={`${testId}-clear`}>Cambiar</Button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5">
        <MagnifyingGlass size={11} /> Cliente <span className="text-[var(--ag-text-dim)] normal-case tracking-normal">(autocompletado CRM)</span>
      </Label>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpenList(true)}
        placeholder="Escribe nombre, matrícula, email o teléfono…"
        className="mt-1 rounded-sm"
        data-testid={`${testId}-input`}
      />
      {openList && (results.length > 0 || q.length >= 2) && (
        <div className="absolute z-30 mt-1 w-full bg-[var(--ag-midnight-card)] border border-[var(--ag-border)] rounded-sm shadow-xl max-h-72 overflow-y-auto" data-testid={`${testId}-results`}>
          {results.map((c) => (
            <button
              type="button" key={c.id}
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 hover:bg-[var(--ag-midnight-soft)] border-b border-[var(--ag-border)] last:border-0 flex items-center gap-2"
              data-testid={`${testId}-result-${c.id}`}
            >
              {c.type === "empresa" ? <Buildings size={14} weight="fill" className="text-[var(--ag-aqua-soft)]" /> : <User size={14} weight="duotone" className="text-[var(--ag-text-dim)]" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{c.name}</div>
                <div className="text-[11px] text-[var(--ag-text-dim)] truncate">
                  {[c.main_license_plate, c.email, c.phone].filter(Boolean).join(" · ") || (c.type === "empresa" ? c.nif_cif : "—")}
                </div>
              </div>
              <CheckCircle size={12} weight="duotone" className="text-emerald-400 shrink-0" />
            </button>
          ))}
          {results.length === 0 && q.length >= 2 && (
            <div className="p-3 text-center">
              <div className="text-xs text-[var(--ag-text-dim)] mb-2">Sin resultados para "{q}"</div>
              <Button type="button" size="sm" onClick={startCreate} className="ag-btn-gold h-8 text-xs" data-testid={`${testId}-create-new`}>
                <Plus size={12} className="mr-1" /> Crear cliente rápido
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Modal creación rápida */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="rounded-sm max-w-md" data-testid={`${testId}-quick-modal`}>
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">Crear cliente rápido</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 p-1 bg-[var(--ag-midnight-soft)] rounded">
              <button type="button" onClick={()=>setForm({...form, type:"particular"})}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded ${form.type==="particular"?"bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold":"text-[var(--ag-text-dim)]"}`}
                data-testid={`${testId}-type-particular`}>
                <User size={12} className="inline mr-1" weight="fill" /> Particular · Ticket
              </button>
              <button type="button" onClick={()=>setForm({...form, type:"empresa"})}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded ${form.type==="empresa"?"bg-[var(--ag-aqua)] text-white font-semibold":"text-[var(--ag-text-dim)]"}`}
                data-testid={`${testId}-type-empresa`}>
                <Buildings size={12} className="inline mr-1" weight="fill" /> Empresa · Factura
              </button>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">{form.type === "empresa" ? "Persona de contacto *" : "Nombre completo *"}</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid={`${testId}-q-name`} />
            </div>
            {form.type === "empresa" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Razón social *</Label>
                  <Input value={form.razon_social} onChange={(e)=>setForm({...form, razon_social: e.target.value})} className="mt-1 rounded-sm" data-testid={`${testId}-q-razon`} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">CIF *</Label>
                  <Input value={form.nif_cif} onChange={(e)=>setForm({...form, nif_cif: e.target.value.toUpperCase()})} className="mt-1 rounded-sm font-mono-tab" data-testid={`${testId}-q-nif`} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Dirección fiscal</Label>
                  <Input value={form.address_fiscal} onChange={(e)=>setForm({...form, address_fiscal: e.target.value})} className="mt-1 rounded-sm" />
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">Email</Label>
                <Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="mt-1 rounded-sm" data-testid={`${testId}-q-email`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Teléfono</Label>
                <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="mt-1 rounded-sm" data-testid={`${testId}-q-phone`} />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Matrícula habitual</Label>
              <Input value={form.main_license_plate} onChange={(e)=>setForm({...form, main_license_plate: e.target.value.toUpperCase()})} className="mt-1 rounded-sm font-mono-tab" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreating(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submitCreate} disabled={busy} className="ag-btn-gold" data-testid={`${testId}-q-submit`}>
              {busy ? "Creando…" : "Crear y vincular"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
